import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import Loader from '../components/Loader/Loader';
import CesoService from '../axios_services/ceso.service';
import { ButtonInsert, ButtonSearch, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalCesoForm from '../components/Modal/ModalCesoForm';
import ModalCesoArchivo from '../components/Modal/ModalCesoArchivo';
import { FileText } from 'lucide-react';
import './Induccion.css';

function CesoCulminacionContrato() {
  const [state] = useContext(AppContext);
  const perfilId = Number(state.user?.perfil_id);
  const esTH = [5, 8, 22, 26].includes(perfilId);

  const [registros, setRegistros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtroPeriodo, setFiltroPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [catalogo, setCatalogo] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [modalForm, setModalForm] = useState({ isOpen: false, row: null });
  const [modalArchivo, setModalArchivo] = useState({ isOpen: false, row: null, campo: null });

  const initialFetchDone = useRef(false);

  const loadCatalogoYUsuarios = async () => {
    const [cat, usr] = await Promise.all([CesoService.catalogo(), CesoService.usuariosOpciones()]);
    if (cat.success) setCatalogo(cat.data);
    if (usr.success) setUsuarios(usr.data || []);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filtroPeriodo) params.periodo = filtroPeriodo;
      const resp = await CesoService.listar(params);
      setRegistros(resp.data || []);
    } catch (error) {
      setRegistros([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      (async () => {
        try {
          await loadCatalogoYUsuarios();
        } catch (e) {
          /* ignore */
        }
        await loadData();
      })();
    }
  }, []);

  const formatFecha = (f) => {
    if (!f) return '-';
    return String(f).split('T')[0];
  };

  const formatFechaHora = (f) => {
    if (!f) return '-';
    const s = String(f);
    return s.includes('T') ? `${s.split('T')[0]} ${s.split('T')[1]?.slice(0, 5) || ''}` : s;
  };

  const renderDocCell = (r, campo, urlPublica, tieneFlag, titulo) => {
    const tiene = Number(tieneFlag) === 1 && urlPublica;
    if (tiene) {
      return (
        <button
          type="button"
          title={`Ver ${titulo}`}
          className="acta-icon-doc acta-icon-doc--ok"
          onClick={() => window.open(urlPublica, '_blank')}
        >
          <FileText size={18} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    return (
      <button
        type="button"
        title={`Subir ${titulo} (PDF)`}
        className="acta-icon-doc acta-icon-doc--falta"
        onClick={() => setModalArchivo({ isOpen: true, row: r, campo })}
      >
        <FileText size={18} strokeWidth={2} aria-hidden />
      </button>
    );
  };

  const th = (min) => ({ minWidth: min, whiteSpace: 'nowrap' });

  if (!esTH) return <div className="caja-flujo-container">No tiene permisos para acceder.</div>;

  if (isLoading && registros.length === 0) return <Loader />;

  return (
    <div className="caja-flujo-container induccion-modulo">
      <h1>REGISTRO DE CESOS</h1>

      <div className="filtros induccion-filtros">
        <div className="campo-fecha">
          <label>Periodo:</label>
          <input type="month" value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)} />
        </div>
      </div>

      <div className="filtros-actions">
        <ButtonSearch onClick={loadData} isLoading={isLoading} />
      </div>

      <div className="nuevo-movimiento-section">
        <ButtonInsert onClick={() => setModalForm({ isOpen: true, row: null })} />
      </div>

      <div className="table-container" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="induccion-table" style={{ minWidth: '1680px' }}>
          <thead>
            <tr>
              <th style={th('200px')}>Colaborador</th>
              <th style={th('160px')}>Tipo de cese</th>
              <th style={th('200px')}>Estado</th>
              <th style={th('150px')}>Fecha creación</th>
              <th style={th('120px')}>Fecha fin</th>
              <th style={th('200px')}>Generado por</th>
              <th style={{ minWidth: '200px', maxWidth: '280px' }}>Observación</th>
              <th style={th('120px')}>Informe</th>
              <th style={th('120px')}>Planilla</th>
              <th style={th('140px')}>Recursos humanos</th>
              <th style={th('120px')}>Certificado</th>
              <th style={th('110px')}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {registros.length > 0 ? (
              registros.map((r) => (
                <tr key={r.id_cese}>
                  <td>{r.nombre_usuario || '-'}</td>
                  <td>{r.tipo_cese_descripcion || '-'}</td>
                  <td>{r.estado_descripcion || r.estado}</td>
                  <td>{formatFechaHora(r.fecha_creacion)}</td>
                  <td>{formatFecha(r.fecha_fin)}</td>
                  <td>{r.nombre_generador || '-'}</td>
                  <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{r.observacion || '-'}</td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'informe', r.url_informe_publica, r.tiene_informe, 'informe')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'planilla', r.url_planilla_publica, r.tiene_planilla, 'planilla')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'rh', r.url_rh_publica, r.tiene_rh, 'recursos humanos')}
                    </div>
                  </td>
                  <td>
                    <div className="acta-cell">
                      {renderDocCell(r, 'certificado', r.url_certificado_publica, r.tiene_certificado, 'certificado')}
                    </div>
                  </td>
                  <td>
                    <div className="induccion-acciones">
                      <ButtonUpdate title="Editar" onClick={() => setModalForm({ isOpen: true, row: r })} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={12} className="sin-data">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <ModalCesoForm
        isOpen={modalForm.isOpen}
        row={modalForm.row}
        usuarios={usuarios}
        tiposCese={catalogo?.tipos_cese || []}
        estadosCulminacion={catalogo?.estados_culminacion || []}
        estadosDespido={catalogo?.estados_despido || []}
        onClose={() => setModalForm({ isOpen: false, row: null })}
        onSave={async (payload) => {
          if (modalForm.row) {
            await CesoService.actualizar(payload);
          } else {
            await CesoService.crear(payload);
          }
          await loadData();
        }}
      />

      <ModalCesoArchivo
        isOpen={modalArchivo.isOpen}
        row={modalArchivo.row}
        campo={modalArchivo.campo}
        onClose={() => setModalArchivo({ isOpen: false, row: null, campo: null })}
        onSave={async ({ id_cese, campo, archivo }) => {
          setIsLoading(true);
          try {
            await CesoService.subirArchivo(id_cese, campo, archivo, state.user?.usuario_id);
            await loadData();
          } finally {
            setIsLoading(false);
          }
        }}
      />
    </div>
  );
}

export default CesoCulminacionContrato;
